import { Link } from 'react-router-dom';
import type { InterventionItem } from '@dcc/shared';
import { TONE_GLYPH, toneFor } from '../lib/tone';
import { relativeTime } from '../lib/format';

/**
 * One item in the intervention strip. It always answers: what is the fact, what
 * is the impact, who owns it, and what is the next action.
 */
const KIND_LABEL: Record<InterventionItem['kind'], string> = {
  INCIDENT: 'incident',
  BLOCKER: 'blocker',
  DECISION: 'decision',
  LEASE: 'captain',
  CONNECTION: 'connection',
  CAPACITY: 'capacity',
};

export function Intervention({ item }: { item: InterventionItem }) {
  const tone = toneFor('severity', item.severity);
  const overdue = item.dueAt !== null && Date.parse(item.dueAt) < Date.now();
  return (
    <article
      className="intervention"
      data-tone={tone}
      aria-label={`${item.severity}: ${item.title}`}
    >
      {/* The chip states the severity, because the severity is what decides its
          colour. What kind of thing it is belongs in the meta line below. */}
      <span className="chip" data-tone={tone}>
        <span className="chip__glyph" aria-hidden="true">
          {TONE_GLYPH[tone]}
        </span>
        {item.severity}
      </span>

      <div>
        <h3 className="intervention__title">{item.title}</h3>
        <p className="intervention__fact">{item.fact}</p>
        <p className="intervention__fact dim">{item.impact}</p>
        <div className="intervention__meta">
          <span>{KIND_LABEL[item.kind]}</span>
          {item.projectName && <span>{item.projectName}</span>}
          <span>owner: {item.ownerName ?? 'unassigned'}</span>
          <span>seen {relativeTime(item.detectedAt)}</span>
          <span className="mono">source {item.source}</span>
          {item.dueAt && (
            <span className={overdue ? 'tone-text' : ''} data-tone={overdue ? 'risk' : undefined}>
              {overdue ? 'overdue since ' : 'due '}
              {relativeTime(item.dueAt)}
            </span>
          )}
          {item.affectedRoutes.length > 0 && <span>routes: {item.affectedRoutes.join(', ')}</span>}
        </div>
      </div>

      <div className="intervention__action">
        <span className="tiny dim">{item.nextAction}</span>
        {item.actionHref && (
          <Link className="btn btn--sm" to={item.actionHref}>
            Open
          </Link>
        )}
      </div>
    </article>
  );
}
